import RadioGroup from '@oncolens/oncolens-ui/lib/components/FormControls/RadioGroup';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import clsx from 'clsx';
import { FaRegCheckCircle, FaRegCircle } from 'react-icons/fa';
import shallow from 'zustand/shallow';
import useRequestFormStore from './store';
import { isBreastOrOvarian } from './domain/PatientHistory';

export function isExplicitlyMale(id: string) {
  return ['92cca881-8aee-48e1-981b-ac8f0e6e19e2', '35ef9920-06ea-4486-b5ca-e8d95bdc4ae7'].includes(
    id
  );
}



function isPrimaryDiseaseBreastOrOvarian(diseases: any[]) {
  const primaryDisease = Object.values(diseases).find((disease) => disease.isPrimary);
  return primaryDisease && isBreastOrOvarian(primaryDisease?.cancerTypeId || '');
}

function PatientHistoryForm() {
  const formKey = 'patientHistory';

  const [sex, getValues] = useRequestFormStore((store) => [
    store.patient.sexId,
    store.getValues,
  ], shallow);

  return (
    <div className="grid grid-cols-8 gap-3">
      <FormField name={`${formKey}.chiefComplaint`} store={useRequestFormStore}>
        {({ value, onChange }) => (
          <TextAreaInput
            id="chiefComplaint"
            htmlName="chiefComplaint"
            label="Chief Complaint"
            value={value}
            onChange={onChange}
            wrapperProps={{ className: 'col-span-8 sm:col-span-4' }}
          />
        )}
      </FormField>

      <FormField name={`${formKey}.medicalHistory`} store={useRequestFormStore}>
        {({ value, onChange, errorMessage }) => (
          <TextAreaInput
            id="medicalHistory"
            htmlName="medicalHistory"
            label="Medical History"
            required
            value={value}
            onChange={onChange}
            error={errorMessage}
            wrapperProps={{ className: 'col-span-8 sm:col-span-4' }}
          />
        )}
      </FormField>

      <FormField name={`${formKey}.familyHistory`} store={useRequestFormStore}>
        {({ value, onChange, errorMessage }) => (
          <TextAreaInput
            id="familyHistory"
            htmlName="familyHistory"
            label="Family History"
            value={value}
            onChange={onChange}
            error={errorMessage}
            wrapperProps={{ className: 'col-span-8 sm:col-span-4' }}
          />
        )}
      </FormField>

      <FormField name={`${formKey}.socialHistory`} store={useRequestFormStore}>
        {({ value, onChange, errorMessage }) => (
          <TextAreaInput
            id="socialHistory"
            htmlName="socialHistory"
            label="Social History"
            value={value}
            onChange={onChange}
            error={errorMessage}
            wrapperProps={{ className: 'col-span-8 sm:col-span-4' }}
          />
        )}
      </FormField>
      {!isExplicitlyMale(sex) && isPrimaryDiseaseBreastOrOvarian(getValues().diseases) ? (
        <>
          <div className={'col-span-8 sm:col-span-4'}>
            <label htmlFor="Pre" className="inline-block mb-2 text-sm font-medium text-gray-700">
							Menopausal Status
            </label>
            <FormField name={`${formKey}.menopausalStatus`} store={useRequestFormStore}>
              {({ value, onChange }) => (
                <RadioGroup
                  className="flex items-center mb-5 space-x-2"
                  selected={value}
                  setSelected={onChange}
                >
                  <RadioGroup.Option id={'Pre'}>
                    {(active, onClick) => (
                      <div
                        data-cy="menopausal-status-pre"
                        className={clsx(
                          'flex items-center w-36 px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
														'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                        onKeyDown={() => onClick()}
                        role="radiogroup"
                        tabIndex={-1}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>Pre</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'Peri'}>
                    {(active, onClick) => (
                      <div
                        data-cy="menopausal-status-peri"
                        className={clsx(
                          'flex items-center w-36 px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
														'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                        onKeyDown={() => onClick()}
                        role="radiogroup"
                        tabIndex={-1}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>Peri</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'Post'}>
                    {(active, onClick) => (
                      <div
                        data-cy="menopausal-status-post"
                        className={clsx(
                          'flex items-center w-36 px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
														'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                        onKeyDown={() => onClick()}
                        role="radiogroup"
                        tabIndex={-1}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>Post</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                </RadioGroup>
              )}
            </FormField>
          </div>
          <div className={'col-span-8 sm:col-span-4'}>
            <label htmlFor={'1'} className="inline-block mb-2 text-sm font-medium text-gray-700">
							Breast or Ovarian Cancer in 1st or 2nd degree relative
            </label>
            <FormField name={`${formKey}.breastOvarianCancerNear`} store={useRequestFormStore}>
              {({ value, onChange }) => (
                <RadioGroup
                  className="flex items-center mb-5 space-x-2"
                  selected={value}
                  setSelected={onChange}
                >
                  <RadioGroup.Option id={1}>
                    {(active, onClick) => (
                      <div
                        data-cy="breast-or-ovarian-cancer-yes"
                        className={clsx(
                          'flex items-center w-36 px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
														'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                        onKeyDown={() => onClick()}
                        role="radiogroup"
                        tabIndex={-1}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>Yes</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={0}>
                    {(active, onClick) => (
                      <div
                        data-cy="breast-or-ovarian-cancer-no"
                        className={clsx(
                          'flex items-center w-36 px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
														'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                        onKeyDown={() => onClick()}
                        role="radiogroup"
                        tabIndex={-1}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>No</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                </RadioGroup>
              )}
            </FormField>
          </div>
        </>
      ) : null}

      <FormField name={`${formKey}.miscellaneousCaseDetails`} store={useRequestFormStore}>
        {({ value, onChange, errorMessage }) => (
          <TextAreaInput
            id="miscellaneousCaseDetails"
            htmlName="miscellaneousCaseDetails"
            label="Miscellaneous Case Details"
            rows={6}
            resize={'vertical'}
            value={value}
            onChange={onChange}
            error={errorMessage}
            wrapperProps={{ className: 'col-span-8' }}
          />
        )}
      </FormField>
    </div>
  );
}

export default PatientHistoryForm;
